/**
 * The notes a sale was really paid in, when they were ANOTHER currency's — a
 * dollar invoice paid in dinars at the till or the web register.
 *
 * The invoice is settled in its own currency, so "Amount received $2.90" is
 * true but tells the customer nothing about the 4,250 dinars they handed over
 * (the price asked, rounded up to 250). The server keeps the notes handed over
 * and the change given back, each in its own currency; this turns them into
 * the three lines a paper prints:
 *
 *   Paid in cash      4,250 IQD   ← the notes that stayed: handed − change
 *   Cash handed over  5,000 IQD   ← only when change came out of the same notes
 *   Change              750 IQD   ← only when there was change
 *
 * A dinar invoice paid with a $100 note keeps the change in dinars, so the
 * paid line is the whole $100 and the change line says 6,250 IQD.
 *
 * Not only notes: a card, wallet or transfer charged in the other currency is
 * recorded the same way with its `method` — "Paid by card 25,500 IQD", one line,
 * since a card machine gives nothing back.
 */
export interface CashFact {
  currencyCode: string;
  amount: string | number;
}

export interface CashNotes {
  /** null = notes (cash); else 'card' | 'wallet' | 'transfer' | 'other'. */
  method: string | null;
  /**
   * What that money paid, in the DOCUMENT's currency — the paper's "i.e. the
   * equivalent of $47.00" beside 80,000 IQD. The invoice's own paid amount
   * (capped at its total): a sale paid in another currency is settled by that
   * one payment. Null when the document does not say what was paid.
   */
  equivalent: number | null;
  currency: string;
  paid: number;
  handed: number | null;
  change: { amount: number; currency: string } | null;
}

/** The document fields that can carry the notes — staff reads and the public paper differ in shape. */
export interface CashCarrier {
  currencyCode: string;
  cashIn?: CashFact | null;
  cashBack?: CashFact | null;
  cashInCurrency?: string | null;
  cashInAmount?: string | number | null;
  cashBackCurrency?: string | null;
  cashBackAmount?: string | number | null;
  /** How cashIn was paid — null/'cash' = notes. */
  cashInMethod?: string | null;
  total?: string | number | null;
  amountPaid?: string | number | null;
}

/** The label key for the "paid" line: `paidInCash`, or `paidByCard` / `paidByWallet`… — `refundedInCash` on a return. */
export function paidLabelKey(method: string | null): 'paidInCash' | 'paidByCard' | 'paidByWallet' | 'paidByTransfer' | 'paidByOther' | 'refundedInCash' {
  if (!method || method === 'cash') return 'paidInCash';
  if (method === 'refund') return 'refundedInCash';
  if (method === 'card') return 'paidByCard';
  if (method === 'wallet') return 'paidByWallet';
  if (method === 'transfer') return 'paidByTransfer';
  return 'paidByOther';
}

/** English fallbacks for those keys. */
export const PAID_LABEL_DEFAULT: Record<ReturnType<typeof paidLabelKey>, string> = {
  paidInCash: 'Paid in cash',
  paidByCard: 'Paid by card',
  paidByWallet: 'Paid by wallet',
  paidByTransfer: 'Paid by transfer',
  paidByOther: 'Paid by other method',
  refundedInCash: 'Refunded in cash',
};

const fact = (obj: CashFact | null | undefined, ccy: string | null | undefined, amount: string | number | null | undefined): CashFact | null =>
  obj ?? (ccy && amount != null ? { currencyCode: ccy, amount } : null);

export function cashNotes(doc: CashCarrier): CashNotes | null {
  const cashIn = fact(doc.cashIn, doc.cashInCurrency, doc.cashInAmount);
  const cashBack = fact(doc.cashBack, doc.cashBackCurrency, doc.cashBackAmount);
  if (!cashIn || cashIn.currencyCode === doc.currencyCode) return null;
  const handed = Number(cashIn.amount);
  if (!Number.isFinite(handed) || handed <= 0) return null;
  const back = cashBack ? Number(cashBack.amount) : 0;
  const hasChange = !!cashBack && Number.isFinite(back) && back > 0;
  const fromSameNotes = hasChange && cashBack.currencyCode === cashIn.currencyCode;
  const method = doc.cashInMethod && doc.cashInMethod !== 'cash' ? doc.cashInMethod : null;
  const paidDoc = Number(doc.amountPaid);
  const totalDoc = Number(doc.total);
  // A return refunded in other notes: what they were worth is the return itself.
  const equivalent = method === 'refund'
    ? (Number.isFinite(totalDoc) && totalDoc > 0 ? totalDoc : null)
    : Number.isFinite(paidDoc) && paidDoc > 0
      ? (Number.isFinite(totalDoc) && totalDoc > 0 ? Math.min(paidDoc, totalDoc) : paidDoc)
      : null;
  return {
    method,
    equivalent,
    currency: cashIn.currencyCode,
    paid: fromSameNotes ? handed - back : handed,
    handed: fromSameNotes ? handed : null,
    change: hasChange ? { amount: back, currency: cashBack.currencyCode } : null,
  };
}
